import { useI18n } from '@/hooks/useI18n';
import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface LogoSelectorProps {
    logoChoice: 'own' | 'agency' | null;
    onLogoChoiceChange: (value: 'own' | 'agency') => void;
    hasOwnLogo: boolean;
    hasAgencyLogo: boolean;
    ownLogoUrl?: string | null;
    ownLogoSmallUrl?: string | null;
    agencyLogoUrl?: string | null;
    agencyLogoSmallUrl?: string | null;
    isPdfGenerating: boolean;
}

export const LogoSelector = ({
    logoChoice,
    onLogoChoiceChange,
    hasOwnLogo,
    hasAgencyLogo,
    ownLogoUrl,
    ownLogoSmallUrl,
    agencyLogoUrl,
    agencyLogoSmallUrl,
    isPdfGenerating
}: LogoSelectorProps) => {
    const { t } = useI18n();
    const [resolvedAgencyLogoUrl, setResolvedAgencyLogoUrl] = useState<string | null>(agencyLogoUrl || null);
    const [resolvedAgencyLogoSmallUrl, setResolvedAgencyLogoSmallUrl] = useState<string | null>(agencyLogoSmallUrl || null);

    useEffect(() => {
        if (hasAgencyLogo && !agencyLogoUrl) {
            Promise.all([
                supabase.rpc('get_my_agency_logo' as any),
                supabase.rpc('get_my_agency_logo_small' as any),
            ]).then(([logoRes, logoSmallRes]) => {
                setResolvedAgencyLogoUrl((logoRes.data as string) || null);
                setResolvedAgencyLogoSmallUrl((logoSmallRes.data as string) || null);
            });
        }
    }, [hasAgencyLogo, agencyLogoUrl]);

    useEffect(() => { setResolvedAgencyLogoUrl(agencyLogoUrl || null); }, [agencyLogoUrl]);
    useEffect(() => { setResolvedAgencyLogoSmallUrl(agencyLogoSmallUrl || null); }, [agencyLogoSmallUrl]);

    const handleLogoClick = (choice: 'own' | 'agency') => {
        if (choice === 'own' && !hasOwnLogo) return;
        if (choice === 'agency' && !hasAgencyLogo) return;
        onLogoChoiceChange(choice);
    };

    const logoOptions = [
        {
            value: 'own' as const,
            label: t('itinerary.ownLogo'),
            available: hasOwnLogo,
            logoUrl: ownLogoUrl,
            logoSmallUrl: ownLogoSmallUrl,
        },
        {
            value: 'agency' as const,
            label: t('itinerary.agencyLogo'),
            available: hasAgencyLogo,
            logoUrl: resolvedAgencyLogoUrl,
            logoSmallUrl: resolvedAgencyLogoSmallUrl,
        }
    ];

    return (
      <div className="space-y-3">
        <div className="text-sm font-medium text-gray-700">
          {t("itinerary.logoChoice")}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {logoOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleLogoClick(option.value)}
              disabled={!option.available || isPdfGenerating}
              className={`
                relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all
                ${
                  !option.available
                    ? "opacity-40 cursor-not-allowed bg-gray-50 border-gray-200"
                    : logoChoice === option.value
                      ? "border-black bg-gray-50 shadow-sm"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }
                ${isPdfGenerating ? "cursor-wait" : ""}
              `}
            >
              {logoChoice === option.value && option.available && (
                <div className="absolute top-2 right-2 bg-black text-white rounded-full p-0.5">
                  <Check className="h-3 w-3" />
                </div>
              )}

              <div className="w-full flex items-center justify-center gap-3 mb-3 min-h-[5rem]">
                {option.logoUrl ? (
                  <img
                    src={option.logoUrl}
                    alt={option.label}
                    className="max-h-16 max-w-[55%] object-contain"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs text-gray-400 text-center px-1">
                      {t("common.noLogo")}
                    </span>
                  </div>
                )}
                {option.logoSmallUrl && (
                  <img
                    src={option.logoSmallUrl}
                    alt={`${option.label} mini`}
                    className="max-h-8 max-w-[35%] object-contain opacity-70"
                  />
                )}
              </div>

              <div className="text-sm font-medium text-gray-700 text-center">
                {option.label}
              </div>

              {!option.available && (
                <div className="text-xs text-gray-400 mt-1">
                  {t("common.notAvailable")}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
};
